import { supabase } from '../supabase.js';
import { PDFDocument } from 'pdf-lib';
import { DOC_KINDS, templateKeys, resolveLearnerDocs, buildLearnerDocsPdf } from './learner-docs-core.mjs';

/* ============================================================
   Learner documents in the browser (docs/learner-documents.md):
   fetch the blank templates, the cohorts and the learner rows,
   resolve, draw, download or store. Drawing lives in
   learner-docs-core.mjs so the server sender can share it.
============================================================ */

const _templates = new Map();   // file in doc-templates/ → bytes, kept while the page is open
const CHUNK = 50;    // learners per learner_document_data call (keeps each call well under the 8 s limit)

/* The backgrounds carry the CEO's signature and ID number, so they live in the
   private "documents" bucket (doc-templates/), readable by learner-pay holders
   only — never in app/public, where anyone could download a signed blank form. */
async function loadTemplates(docs, kinds = DOC_KINDS) {
  const out = {};
  await Promise.all([...templateKeys(docs, kinds)].map(async ([key, file]) => {
    if (!_templates.has(file)) {
      const { data, error } = await supabase.storage.from('documents').download(`doc-templates/${file}`);
      if (error) throw new Error(`Template ${file} could not be loaded: ${error.message}`);
      _templates.set(file, await data.arrayBuffer());
    }
    out[key] = _templates.get(file);
  }));
  return out;
}

export async function loadCohorts() {
  const { data, error } = await supabase
    .from('cohorts')
    .select('cohort_id,name,period_start,period_end,is_default,config,updated_at')
    .order('cohort_id');
  if (error) throw new Error(error.message);
  return data || [];
}

/** learner_document_data for many learners, in chunks: student_uuid → doc. */
export async function loadDocData(studentUuids) {
  const out = new Map();
  for (let i = 0; i < studentUuids.length; i += CHUNK) {
    const { data, error } = await supabase.rpc('learner_document_data', { p_student_uuids: studentUuids.slice(i, i + CHUNK) });
    if (error) throw new Error(error.message);
    for (const r of data || []) out.set(r.student_uuid, r.doc);
  }
  return out;
}

async function resolveFor(studentUuids, opt = {}) {
  const [cohorts, sys, learnerRes] = await Promise.all([
    opt.cohortOverride ? Promise.resolve([]) : loadCohorts(),
    loadDocData(studentUuids),
    supabase.from('students').select('student_uuid,first_name,surname,national_id').in('student_uuid', studentUuids),
  ]);
  if (learnerRes.error) throw new Error(learnerRes.error.message);
  return resolveLearnerDocs({
    studentUuids, cohorts, learners: learnerRes.data,
    sysRows: [...sys].map(([student_uuid, doc]) => ({ student_uuid, doc })),
    cohortOverride: opt.cohortOverride ?? null,
  });
}

/**
 * Resolve and draw documents with the current values (nothing stored).
 * @param {string[]} studentUuids
 * @param {object}   [opt]
 * @param {string[]} [opt.kinds]          which documents (default all three)
 * @param {object}   [opt.cohortOverride] an unsaved cohort row (Set-up preview)
 * @returns {Promise<{ bytes: Uint8Array|null, docs: object[], skipped: {student_uuid, reason}[] }>}
 */
export async function buildLearnerDocs(studentUuids, opt = {}) {
  const kinds = opt.kinds?.length ? opt.kinds : DOC_KINDS;
  const { docs, skipped } = await resolveFor(studentUuids, opt);
  const bytes = docs.length ? await buildLearnerDocsPdf(docs, await loadTemplates(docs, kinds), kinds) : null;
  return { bytes, docs, skipped };
}

export const storedPath = (studentUuid) => `learners/${studentUuid}.pdf`;

/**
 * Build each learner's three documents with the current values and store them
 * (documents/learners/<uuid>.pdf + a learner_documents row with what was printed).
 * Saving again replaces the file and the row; the audit log keeps the history.
 */
export async function generateAndStore(studentUuids, onProgress = () => {}) {
  const { data: { session } } = await supabase.auth.getSession();
  const saved = [], failed = [];
  let skipped = [];
  for (let i = 0; i < studentUuids.length; i += CHUNK) {
    const part = await resolveFor(studentUuids.slice(i, i + CHUNK));
    skipped = skipped.concat(part.skipped);
    for (const d of part.docs) {
      try {
        const bytes = await buildLearnerDocsPdf([d], await loadTemplates([d]));
        const path = storedPath(d.student_uuid);
        const up = await supabase.storage.from('documents')
          .upload(path, new Blob([bytes], { type: 'application/pdf' }), { upsert: true, contentType: 'application/pdf' });
        if (up.error) throw new Error(up.error.message);
        const { send_with_payslips, ...snapshot } = d;
        const row = await supabase.from('learner_documents').upsert({
          student_uuid: d.student_uuid, cohort_id: d.cohort_id, period_start: d.period_start, period_end: d.period_end,
          pdf_path: path, snapshot, generated_at: new Date().toISOString(),
          generated_by: session?.user?.id ?? null,
        }, { onConflict: 'student_uuid' });
        if (row.error) throw new Error(row.error.message);
        saved.push(d.student_uuid);
      } catch (e) {
        failed.push({ student_uuid: d.student_uuid, reason: e.message });
      }
      onProgress(saved.length + failed.length + skipped.length, studentUuids.length);
    }
  }
  return { saved, skipped, failed };
}

/** The stored PDFs of some learners, merged into one file (in the order given). */
export async function downloadStored(rows) {
  const files = [];
  for (const r of rows) {
    const { data, error } = await supabase.storage.from('documents').download(r.pdf_path);
    if (error) throw new Error(`${r.pdf_path}: ${error.message}`);
    files.push(new Uint8Array(await data.arrayBuffer()));
  }
  if (files.length === 1) return files[0];
  const out = await PDFDocument.create();
  for (const f of files) {
    const src = await PDFDocument.load(f);
    for (const p of await out.copyPages(src, src.getPageIndices())) out.addPage(p);
  }
  return out.save();
}

export function downloadPdf(bytes, filename) {
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

/** "Thandeka Example" → "thandeka-example" for file names. */
export function slug(s) {
  return String(s || '').toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'learner';
}
